import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import { CreateChancel, Input, TextareaCustom, SelectTypeAutocomplete } from 'components';
import {
    FindLoad,
    FindSuccessItem,
    useModal,
    KeyPurpose,
    MAX_BYTES_BY_PURPOSE,
    CONTENT_TYPE_ALLOWLIST_BY_PURPOSE,
    INSTRUCTIONS_MAX_LENGTH,
    OrgIntegrations,
} from 'utils';
import { formActions, httpRequestsOnSuccessActions, uploadFile } from 'store';
import { transformNotesObject } from '../formDetail/constants';
import { normalizeFormIds } from '../utils/normalizeFormIds';
import { formBuilderStatusEnums, formModelEnums, IntakeForm } from '../constants';
import { GenerateWithAi } from './generateFromFile';

const ACTION_TYPE = 'CREATE_FORM';

export const CreateFormModal = ({ duplicateData }) => {
    const isDuplicate = !!duplicateData;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { close } = useModal();
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const formSettings = useSelector((state) => state.forms.formSettings);
    const models = formSettings?.models || [];

    const { handleSubmit, control, setValue } = useForm({
        defaultValues: {
            title: duplicateData?.title ? `${duplicateData.title} (Copy)`.slice(0, 60) : '',
            description: duplicateData?.description || '',
            aiPrompt: '',
        },
    });
    // The builder used `models[0]` as the singular model when duplicating; mirror that here.
    const [model, setModel] = useState(duplicateData?.models?.[0] || '');
    const [modelError, setModelError] = useState('');
    // AI generation: a PDF plus optional instructions (`aiPrompt` form field).
    // Both are only shown while "Generate from file" is on.
    const [aiEnabled, setAiEnabled] = useState(false);
    // Org-wide AI switch (admin portal → AI Bridge). Off: generation is not offered at all.
    const { aiBridge } = OrgIntegrations();
    const [sourceFile, setSourceFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [uploading, setUploading] = useState(false);
    // Survives the redux round-trip so the success effect below can hand the
    // request to the builder page it navigates to. Null when the author did not
    // ask for generation; otherwise carries whichever of the two inputs they gave.
    const aiRequestRef = useRef(null);

    useEffect(() => {
        dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        if (!formSettings) dispatch(formActions.getFormSettings());
    }, []);

    useEffect(() => {
        if (success?.type && success?.data?.data?.id) {
            const id = success.data.data.id;
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            close();
            navigate(`/template/${id}`, { state: aiRequestRef.current ? { aiGenerate: aiRequestRef.current } : undefined });
        }
    }, [success]);

    const acceptFile = (file) => {
        if (!file) return;

        if (!CONTENT_TYPE_ALLOWLIST_BY_PURPOSE[KeyPurpose.FORM_AI_SOURCE].includes(file.type)) {
            setFileError('Upload a PDF. Export Word documents or images to PDF first.');
            return;
        }
        if (file.size > MAX_BYTES_BY_PURPOSE[KeyPurpose.FORM_AI_SOURCE]) {
            setFileError('File is too large. Maximum size is 20 MB.');
            return;
        }
        setFileError('');
        setSourceFile(file);
    };

    const toggleAi = () => {
        const next = !aiEnabled;
        setAiEnabled(next);
        if (!next) {
            setSourceFile(null);
            setValue('aiPrompt', '');
            setFileError('');
        }
    };

    const clearFile = () => {
        setSourceFile(null);
        setFileError('');
    };

    const handleCreate = async (values) => {
        if (!model) {
            setModelError('Use case is required.');
            return;
        }

        const aiPrompt = values.aiPrompt?.trim();
        if (aiEnabled && !sourceFile && !aiPrompt) {
            setFileError('Upload a document, describe the template you want, or both.');
            return;
        }
        // The counter in the AI block already shows how far over they are, in red.
        // Blocking here keeps the api from rejecting a request the UI could see
        // was invalid, without repeating the number in a second message.
        if (aiEnabled && aiPrompt && aiPrompt.length > INSTRUCTIONS_MAX_LENGTH) return;

        // Upload before creating. If S3 rejects the document we have not yet
        // created a template, so the author retries from a clean modal instead
        // of being dropped into an empty builder that was supposed to fill
        // itself. Duplicates never carry a source document.
        let uploadedFileKey;
        if (aiEnabled && sourceFile && !isDuplicate) {
            try {
                setUploading(true);
                const stored = await uploadFile(sourceFile, KeyPurpose.FORM_AI_SOURCE);
                uploadedFileKey = stored?.key || undefined;
            } catch (e) {
                setFileError('Upload failed. Please try again.');
                return;
            } finally {
                setUploading(false);
            }
        }

        // Handed to the builder page on navigate. A run needs one of the two, not
        // both, so the request carries whichever the author supplied.
        aiRequestRef.current =
            aiEnabled && !isDuplicate && (uploadedFileKey || aiPrompt)
                ? { fileKey: uploadedFileKey, prompt: aiPrompt || undefined }
                : null;

        // Duplicate: create a new template from the copied content (sections,
        // header/footer, settings) under a fresh title/use-case. Existing section
        // and attribute ids are preserved and only missing ones are backfilled —
        // the same normalization the builder applied to duplicates before the
        // create flow moved into this modal.
        if (isDuplicate) {
            const normalized = normalizeFormIds(cloneDeep(duplicateData));
            dispatch(
                formActions.createForm({
                    title: values.title,
                    description: values.description || '',
                    model,
                    status: formBuilderStatusEnums.UNPUBLISHED,
                    header: duplicateData?.header || { content: '', properties: {}, show: false },
                    footer: duplicateData?.footer || { content: '', properties: {}, show: false },
                    formSettings: duplicateData?.formSettings || {},
                    sections: normalized?.sections || [{ id: uuidv4(), name: 'Information', attributes: [] }],
                }),
            );
            return;
        }

        const sections =
            model === formModelEnums.INTAKEFORM
                ? IntakeForm
                : [{ id: uuidv4(), name: 'Information', attributes: [] }];

        dispatch(
            formActions.createForm({
                title: values.title,
                description: values.description || '',
                model,
                status: formBuilderStatusEnums.UNPUBLISHED,
                header: { content: '', properties: {}, show: false },
                footer: { content: '', properties: {}, show: false },
                formSettings: {},
                sections,
            }),
        );
    };

    return (
        <div className="create-template-modal">
            <p className="modal-header-title">{isDuplicate ? 'Duplicate Template' : 'Create Template'}</p>
            <form onSubmit={handleSubmit(handleCreate)}>
                <div className="modal-body-wrapper">
                    <Input
                        name="title"
                        label="Title*"
                        variant="outlined"
                        control={control}
                        maxLength={60}
                        rules={{
                            required: 'Title is required.',
                            maxLength: { value: 60, message: 'Max length is 60.' },
                        }}
                    />
                    <SelectTypeAutocomplete
                        name="model"
                        handleSelect={(e) => {
                            setModel(e?.target?.value);
                            setModelError('');
                        }}
                        defaultValue={model}
                        list={models ? transformNotesObject(models) : []}
                        title="Use Case *"
                        renderValue={(i) => i?.name}
                        wideMenu
                        error={modelError ? 'model' : ''}
                        typeError={modelError}
                    />
                    <TextareaCustom
                        control={control}
                        name="description"
                        label="Description"
                        variant="outlined"
                        allowCharacter={true}
                    />

                    {!isDuplicate && aiBridge && (
                        <GenerateWithAi
                            control={control}
                            enabled={aiEnabled}
                            onToggle={toggleAi}
                            sourceFile={sourceFile}
                            onFileSelect={acceptFile}
                            onFileClear={clearFile}
                            fileError={fileError}
                        />
                    )}
                </div>
                <CreateChancel
                    type="submit"
                    loader={!!loader.length || uploading}
                    create={isDuplicate ? 'Duplicate' : aiEnabled ? 'Create & Generate' : 'Create'}
                    chancel="Cancel"
                    onClose={close}
                    buttonWidth="100%"
                />
            </form>
        </div>
    );
};
